import { createTheme } from '@mui/material/styles';

// Create theme function that accepts mode (light/dark)
// Using MUI Dashboard template colors with black gloss dark theme
export const createAppTheme = (mode = 'light') => {
  const isDark = mode === 'dark';

  return createTheme({
    palette: {
      mode,
      primary: {
        main: isDark ? '#6366f1' : '#1976d2', // MUI blue for light, indigo for dark
        light: isDark ? '#818cf8' : '#42a5f5',
        dark: isDark ? '#4f46e5' : '#1565c0',
        contrastText: '#fff',
      },
      secondary: {
        main: isDark ? '#8b5cf6' : '#9c27b0',
        light: isDark ? '#a78bfa' : '#ba68c8',
        dark: isDark ? '#7c3aed' : '#7b1fa2',
      },
      background: {
        default: isDark ? '#121212' : '#f5f7fa', // Dark gray instead of pure black
        paper: isDark ? '#1a1a1a' : '#ffffff', // Charcoal for cards
        elevated: isDark ? '#242424' : '#f8fafc',
      },
      text: {
        primary: isDark ? '#ffffff' : '#1a202c',
        secondary: isDark ? '#a0aec0' : '#4a5568',
        disabled: isDark ? '#718096' : '#a0aec0',
      },
      divider: isDark ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
      success: {
        main: '#2563eb',
        light: '#3b82f6',
        dark: '#1d4ed8',
      },
      warning: {
        main: '#f59e0b',
        light: '#fbbf24',
        dark: '#d97706',
      },
      error: {
        main: '#ef4444',
        light: '#f87171',
        dark: '#dc2626',
      },
      info: {
        main: '#3b82f6',
        light: '#60a5fa',
        dark: '#2563eb',
      },
    },
    typography: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      h1: {
        fontSize: '2rem',
        fontWeight: 700,
        lineHeight: 1.2,
      },
      h2: {
        fontSize: '1.75rem',
        fontWeight: 700,
        lineHeight: 1.2,
      },
      h3: {
        fontSize: '1.5rem',
        fontWeight: 600,
        lineHeight: 1.3,
      },
      h4: {
        fontSize: '1.25rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h5: {
        fontSize: '1.125rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h6: {
        fontSize: '1rem',
        fontWeight: 600,
        lineHeight: 1.5,
      },
      body1: {
        fontSize: '0.875rem',
        lineHeight: 1.6,
      },
      body2: {
        fontSize: '0.8125rem',
        lineHeight: 1.6,
      },
      button: {
        textTransform: 'none',
        fontWeight: 600,
      },
    },
    shape: {
      borderRadius: 8,
    },
    shadows: [
      'none',
      isDark 
        ? '0 1px 3px rgba(0, 0, 0, 0.5)' 
        : '0 1px 2px rgba(0, 0, 0, 0.05)',
      isDark
        ? '0 4px 6px rgba(0, 0, 0, 0.5)'
        : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      isDark
        ? '0 10px 15px rgba(0, 0, 0, 0.5)'
        : '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
      isDark
        ? '0 20px 25px rgba(0, 0, 0, 0.6)'
        : '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
      ...Array(20).fill('none'),
    ],
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: isDark
              ? '0 1px 3px rgba(0, 0, 0, 0.5)'
              : '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
            border: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : 'none',
            backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            padding: '8px 16px',
            fontWeight: 600,
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: isDark
                ? '0 4px 12px rgba(99, 102, 241, 0.4)'
                : '0 2px 8px rgba(25, 118, 210, 0.3)',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: isDark
              ? '1px solid rgba(255, 255, 255, 0.08)'
              : '1px solid #e2e8f0',
          },
          head: {
            backgroundColor: isDark ? '#242424' : '#f7fafc',
            fontWeight: 600,
            fontSize: '0.8125rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: isDark ? '#cbd5e1' : '#4a5568',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f7fafc',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            fontWeight: 500,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
            color: isDark ? '#ffffff' : '#1a202c',
            boxShadow: isDark
              ? '0 1px 3px rgba(0, 0, 0, 0.5)'
              : '0 1px 3px rgba(0, 0, 0, 0.12)',
            borderBottom: isDark
              ? '1px solid rgba(255, 255, 255, 0.08)'
              : '1px solid #e2e8f0',
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            // Prevent mobile browser zoom on focus/autofill (iOS Safari zooms when font-size < 16px)
            '@media (max-width: 600px)': {
              fontSize: '16px !important',
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              backgroundColor: isDark ? '#242424' : '#f7fafc',
              color: isDark ? '#ffffff' : '#1a202c',
              '& fieldset': {
                borderColor: isDark ? 'rgba(255, 255, 255, 0.12)' : '#e2e8f0',
              },
              '&:hover fieldset': {
                borderColor: isDark ? '#6366f1' : '#1976d2',
              },
              '&.Mui-focused fieldset': {
                borderColor: isDark ? '#818cf8' : '#1976d2',
                borderWidth: '2px',
              },
            },
            '& .MuiInputLabel-root': {
              color: isDark ? '#cbd5e1' : '#718096',
              '&.Mui-focused': {
                color: isDark ? '#818cf8' : '#1976d2',
              },
            },
            '& .MuiInputBase-input': {
              color: isDark ? '#ffffff' : '#1a202c',
              // Prevent mobile browser zoom on focus (iOS requires >= 16px)
              '@media (max-width: 600px)': {
                fontSize: '16px !important',
              },
            },
            '& .MuiInputBase-input::placeholder': {
              color: isDark ? '#64748b' : '#a0aec0',
              opacity: 1,
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#242424' : '#f7fafc',
            color: isDark ? '#ffffff' : '#1a202c',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? 'rgba(255, 255, 255, 0.12)' : '#e2e8f0',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? '#6366f1' : '#1976d2',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? '#818cf8' : '#1976d2',
              borderWidth: '2px',
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
            color: isDark ? '#ffffff' : '#1a202c',
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#f7fafc',
            },
            '&.Mui-selected': {
              backgroundColor: isDark ? 'rgba(99, 102, 241, 0.2)' : 'rgba(25, 118, 210, 0.08)',
              color: isDark ? '#818cf8' : '#1976d2',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(99, 102, 241, 0.25)' : 'rgba(25, 118, 210, 0.12)',
              },
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: isDark ? '#cbd5e1' : '#718096',
            '&.Mui-focused': {
              color: isDark ? '#818cf8' : '#1976d2',
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            color: isDark ? '#718096' : '#718096',
          },
        },
      },
    },
  });
};
